import { Text, BlockStack, Link } from "@shopify/polaris";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type ProjectContactFields = {
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactLinkedin?: string | null;
  contactWhatsapp?: string | null;
  contactViber?: string | null;
};

function digitsOnly(value: string): string {
  return value.replace(/[^\d+]/g, "");
}

function linkedinHref(value: string): string {
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/linkedin\.com/i.test(trimmed)) return `https://${trimmed.replace(/^\/+/, "")}`;
  return `https://www.linkedin.com/in/${trimmed.replace(/^@/, "")}`;
}

function whatsappHref(value: string): string {
  const digits = digitsOnly(value).replace(/^\+/, "");
  return `https://wa.me/${digits}`;
}

function ContactRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Text as="p">
      {label}: {children}
    </Text>
  );
}

/**
 * Read-only client contact block for the project detail sidebar.
 */
export default function ProjectContactSummary({
  contactName,
  contactEmail,
  contactPhone,
  contactLinkedin,
  contactWhatsapp,
  contactViber,
}: ProjectContactFields) {
  const { t } = useTranslation();

  const hasAny =
    contactName ||
    contactEmail ||
    contactPhone ||
    contactLinkedin ||
    contactWhatsapp ||
    contactViber;

  if (!hasAny) {
    return (
      <Text as="p" tone="subdued">
        {t("project_contact_empty", {
          defaultValue: "No client contact details yet.",
        })}
      </Text>
    );
  }

  return (
    <BlockStack gap="100">
      <Text as="h3" variant="headingSm">
        {t("project_contact", { defaultValue: "Client contact" })}
      </Text>
      {contactName && (
        <ContactRow label={t("contact-name", { defaultValue: "Name" })}>
          {contactName}
        </ContactRow>
      )}
      {contactEmail && (
        <ContactRow label={t("email", { defaultValue: "Email" })}>
          <Link url={`mailto:${contactEmail}`}>{contactEmail}</Link>
        </ContactRow>
      )}
      {contactPhone && (
        <ContactRow label={t("phone", { defaultValue: "Phone" })}>
          <Link url={`tel:${digitsOnly(contactPhone)}`}>{contactPhone}</Link>
        </ContactRow>
      )}
      {contactLinkedin && (
        <ContactRow label={t("linkedin", { defaultValue: "LinkedIn" })}>
          <Link url={linkedinHref(contactLinkedin)} external>
            {contactLinkedin}
          </Link>
        </ContactRow>
      )}
      {contactWhatsapp && (
        <ContactRow label={t("whatsapp", { defaultValue: "WhatsApp" })}>
          <Link url={whatsappHref(contactWhatsapp)} external>
            {contactWhatsapp}
          </Link>
        </ContactRow>
      )}
      {contactViber && (
        <ContactRow label={t("viber", { defaultValue: "Viber" })}>
          {contactViber}
        </ContactRow>
      )}
    </BlockStack>
  );
}
